/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './OwnershipTreeCard.module.scss';

import type { OwnershipTreeCardProps } from './OwnershipTreeCard.types';

export const OwnershipTreeCard = ({ className }: OwnershipTreeCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const played = useIntersection(ref, { threshold: 0.35 });

  return (
    <div ref={ref} className={cn(styles.card, played && styles.play, className)}>
      <div className={styles.head}>
        <span className={styles.dot} />
        <span className={styles.headTitle}>Ownership graph — Acme Group</span>
      </div>

      <div className={styles.body}>
        <svg
          className={styles.canvas}
          viewBox="0 0 420 330"
          role="img"
          aria-label="Ownership structure: Acme Group Holdings Ltd wholly owns Acme Holdings GmbH and Acme Asia Pte. Ltd., which in turn owns 80% of Acme Manufacturing Sp. z o.o.; R. Santos holds 62% as ultimate beneficial owner."
        >
          <path className={cn(styles.edge, styles.edge1)} pathLength={100} d="M210 152 L120 152 L120 192" />
          <path className={cn(styles.edge, styles.edge2)} pathLength={100} d="M210 152 L306 152 L306 192" />
          <path className={cn(styles.edge, styles.edge3)} pathLength={100} d="M120 240 L120 268" />
          <path className={cn(styles.edge, styles.edgeUp)} pathLength={100} d="M210 104 L210 56" />

          <text className={cn(styles.pct, styles.pct1)} x="86" y="186">
            100%
          </text>
          <text className={cn(styles.pct, styles.pct2)} x="316" y="186">
            100%
          </text>
          <text className={cn(styles.pct, styles.pct3)} x="130" y="262">
            80%
          </text>
          <text className={cn(styles.pct, styles.pct4)} x="220" y="84">
            62% · UBO
          </text>

          <g className={cn(styles.node, styles.nodeRoot)}>
            <rect x="118" y="104" width="184" height="48" rx="10" />
            <text className={styles.nodeTitle} x="134" y="124">
              Acme Group Holdings Ltd
            </text>
            <text className={styles.nodeMeta} x="134" y="140">
              UK · ultimate parent
            </text>
          </g>

          <g className={cn(styles.node, styles.nodeCompany, styles.nodeSub1)}>
            <rect x="34" y="192" width="172" height="48" rx="10" />
            <text className={styles.nodeTitle} x="48" y="212">
              Acme Holdings GmbH
            </text>
            <text className={styles.nodeMeta} x="48" y="228">
              DE · HRB 184 220
            </text>
          </g>

          <g className={cn(styles.node, styles.nodeCompany, styles.nodeSub2)}>
            <rect x="222" y="192" width="168" height="48" rx="10" />
            <text className={styles.nodeTitle} x="236" y="212">
              Acme Asia Pte. Ltd.
            </text>
            <text className={styles.nodeMeta} x="236" y="228">
              SG · ACRA
            </text>
          </g>

          <g className={cn(styles.node, styles.nodeCompany, styles.nodeGrandchild)}>
            <rect x="34" y="268" width="196" height="48" rx="10" />
            <text className={styles.nodeTitle} x="48" y="288">
              Acme Manufacturing Sp. z o.o.
            </text>
            <text className={styles.nodeMeta} x="48" y="304">
              PL · KRS
            </text>
          </g>

          <g className={cn(styles.node, styles.nodePerson)}>
            <rect x="108" y="18" width="205" height="38" rx="19" />
            <circle cx="132" cy="37" r="10" />
            <text className={styles.personInitials} x="132" y="37">
              RS
            </text>
            <text className={styles.personName} x="150" y="34">
              R. Santos
            </text>
            <text className={styles.personRole} x="150" y="47">
              Ultimate beneficial owner
            </text>
          </g>
        </svg>
      </div>

      <div className={styles.foot}>
        <span className={styles.pill}>UBO resolved · every link filed</span>
      </div>
    </div>
  );
};
